import { getGoogleMapsApiKey } from './api-keys';

export type GoogleMapsLibrary = 'maps' | 'places';

const DEFAULT_LIBRARIES: GoogleMapsLibrary[] = ['maps', 'places'];
const CALLBACK_NAME = '__voahGoogleMapsReady';

let isLoading = false;
let loadPromise: Promise<void> | null = null;

const callbacks: (() => void)[] = [];

function needsPlaces(libraries: GoogleMapsLibrary[]): boolean {
  return libraries.includes('places');
}

/** API pronta para uso (Map construtor + places opcional). */
export function isGoogleMapsReady(libraries: GoogleMapsLibrary[] = DEFAULT_LIBRARIES): boolean {
  const maps = window.google?.maps;
  if (!maps || typeof maps.Map !== 'function') return false;
  if (needsPlaces(libraries) && !maps.places) return false;
  return true;
}

function waitForReady(libraries: GoogleMapsLibrary[], timeoutMs = 15000): Promise<void> {
  if (isGoogleMapsReady(libraries)) return Promise.resolve();

  return new Promise((resolve, reject) => {
    const started = Date.now();
    const tick = () => {
      if (isGoogleMapsReady(libraries)) {
        resolve();
        return;
      }
      if (Date.now() - started >= timeoutMs) {
        reject(new Error('API do Google Maps não disponível após carregamento do script'));
        return;
      }
      window.setTimeout(tick, 50);
    };
    tick();
  });
}

function notifyCallbacks() {
  callbacks.forEach(cb => cb());
  callbacks.length = 0;
}

function failLoad(reject: (error: Error) => void, error: Error) {
  isLoading = false;
  loadPromise = null;
  reject(error);
}

export interface LoadGoogleMapsOptions {
  /** Padrão: maps + places. HotelMap usa só `['maps']`. */
  libraries?: GoogleMapsLibrary[];
}

/**
 * Carrega a API do Google Maps (padrão callback do Google — sem loading=async).
 */
export function loadGoogleMapsApi(options?: LoadGoogleMapsOptions): Promise<void> {
  const libraries = options?.libraries ?? DEFAULT_LIBRARIES;

  if (isGoogleMapsReady(libraries)) {
    return Promise.resolve();
  }

  if (isLoading && loadPromise) {
    return loadPromise.then(() => waitForReady(libraries));
  }

  isLoading = true;

  loadPromise = new Promise<void>((resolve, reject) => {
    const apiKey = getGoogleMapsApiKey();
    if (!apiKey) {
      failLoad(reject, new Error('Google API Key não configurada'));
      return;
    }

    const finish = () => {
      waitForReady(libraries)
        .then(() => {
          isLoading = false;
          resolve();
          notifyCallbacks();
        })
        .catch(err => {
          failLoad(
            reject,
            err instanceof Error
              ? err
              : new Error('API do Google Maps não disponível após carregamento do script'),
          );
        });
    };

    const existingScript = document.querySelector('script[data-voah-maps-loader]');
    if (existingScript) {
      finish();
      return;
    }

    type WindowWithCallback = Window & { [CALLBACK_NAME]?: () => void };
    const win = window as WindowWithCallback;

    win[CALLBACK_NAME] = () => {
      delete win[CALLBACK_NAME];
      finish();
    };

    const script = document.createElement('script');
    script.id = 'google-maps-script';
    script.dataset.voahMapsLoader = '1';
    script.async = true;
    script.defer = true;
    script.src =
      `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}` +
      `&libraries=places&callback=${CALLBACK_NAME}`;

    script.onerror = () => {
      delete win[CALLBACK_NAME];
      script.remove();
      failLoad(reject, new Error('Erro ao carregar a API do Google Maps'));
    };

    document.head.appendChild(script);
  });

  return loadPromise;
}

/** Só mapa (marcadores) — não exige `places`. */
export function loadGoogleMapsCore(): Promise<void> {
  return loadGoogleMapsApi({ libraries: ['maps'] });
}

export function isGoogleMapsLoaded(libraries: GoogleMapsLibrary[] = DEFAULT_LIBRARIES): boolean {
  return isGoogleMapsReady(libraries);
}

export function onGoogleMapsLoaded(
  callback: () => void,
  libraries: GoogleMapsLibrary[] = DEFAULT_LIBRARIES,
): void {
  if (isGoogleMapsReady(libraries)) {
    callback();
  } else {
    callbacks.push(callback);
  }
}

export function cleanupGoogleMapsResources(): void {
  document.querySelectorAll('script[data-voah-maps-loader]').forEach(script => script.remove());

  const win = window as Window & { [CALLBACK_NAME]?: () => void; google?: typeof google };
  delete win[CALLBACK_NAME];
  delete win.google;

  isLoading = false;
  loadPromise = null;
  callbacks.length = 0;
}
